import React, {useEffect, useMemo} from 'react';
import {Form, Formik} from 'formik';
import {FormattedMessage} from 'react-intl';
import moment from 'moment';
import {useLocation, useNavigate} from 'react-router-dom';
import * as Yup from 'yup';
import BaseForm from '../../components/shared/form-controls/base-form';
import {DropdownField} from '../../components/shared/nextgen/dropdown-field';
import {FormNumberInput} from '../../components/shared/nextgen/form/form-number-input';
import FormDateSelector from '../../components/shared/nextgen/form/form-date-selector';
import {FormTextarea} from '../../components/shared/nextgen/form/form-textarea';
import HttpClient from '../../api/api';
import {getAllEmployees} from '../../features/employees/short';
import {canManageOtherTimeBank} from '../../features/profile/entityPermissions';
import {REmployees} from '../../features/roles/types';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';
import {Lx} from '../../i18n/consts';
import {NAVIGATION_ROUTES} from '../../routes/NavigationRoute';

interface LocationState {
    employeeId?: number;
}

interface ManualTransactionForm {
    employee: REmployees | null;
    date: string;
    hours: number;
    comment: string;
}

export default function CreateTimeBankManualTransactionPage() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as LocationState | undefined;
    const profile = useAppSelector((selector) => selector.profile);
    const employees = useAppSelector((selector) => selector.rEmployees);
    const canViewPage = canManageOtherTimeBank(profile);
    const canCreateForOtherEmployees = canManageOtherTimeBank(profile);
    const employeeOptions = useMemo(
        () => (canCreateForOtherEmployees ? employees : []),
        [canCreateForOtherEmployees, employees]
    );
    const selectedEmployee = useMemo(
        () => {
            const targetEmployeeId = state?.employeeId;
            return employeeOptions.find((employee) => employee.id === targetEmployeeId)
                ?? employeeOptions[0]
                ?? null;
        },
        [employeeOptions, state?.employeeId]
    );
    const initialValues = useMemo<ManualTransactionForm>(() => ({
        employee: selectedEmployee,
        date: moment().format('YYYY-MM-DD'),
        hours: 0,
        comment: '',
    }), [selectedEmployee]);

    const validationSchema: Yup.Schema<ManualTransactionForm> = useMemo(() => Yup.object().shape({
        employee: Yup.mixed<REmployees>().nullable().required('Employee is required'),
        date: Yup.string().required('Date is required'),
        hours: Yup.number()
            .required('Hours are required')
            .integer('Hours must be whole hours')
            .notOneOf([0], 'Hours cannot be zero'),
        comment: Yup.string().max(1000).default(''),
    }), []);

    useEffect(() => {
        if (!canViewPage) {
            navigate(NAVIGATION_ROUTES.MAIN, {replace: true});
            return;
        }

        if (employees.length < 1) {
            dispatch(getAllEmployees());
        }
    }, [canViewPage, dispatch, employees.length, navigate]);

    const handleSubmit = async (values: ManualTransactionForm) => {
        if (!values.employee) {
            return;
        }

        await HttpClient.timeBankApi.createManualTransaction(values.employee.id, {
            date: values.date,
            hours: values.hours,
            comment: values.comment.trim() || undefined,
        });
    };

    if (!canViewPage || !initialValues.employee) {
        return null;
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur
            validateOnChange
            enableReinitialize
            onSubmit={handleSubmit}
        >
            {() => (
                <Form>
                    <BaseForm
                        contentGap={24}
                        formType='create'
                        formHeader='timebankreport_manual_transaction'
                        formHeaderDefaultMessage='Time bank transaction'
                        savingTitle='Time bank transaction'
                        onClose={() => navigate(NAVIGATION_ROUTES.TIMEBANKREPORT)}
                        onAfterSave={() => navigate(NAVIGATION_ROUTES.TIMEBANKREPORT)}
                        allowPristineSubmit
                    >
                        <div style={{display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '500px'}}>
                            <p style={{margin: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '22px'}}>
                                This transaction changes the time bank balance directly. It will appear only in Time Bank Report; no records will be added to Time Sheet or Time Report, and their totals will not change.
                            </p>
                            <DropdownField
                                label='Employee'
                                name='employee'
                                items={employeeOptions}
                                required
                                placeholder='Choose employee'
                                disabled={!canCreateForOtherEmployees}
                            />
                            <FormDateSelector
                                label='Date'
                                name='date'
                                required
                            />
                            <FormNumberInput
                                label={<FormattedMessage id={Lx.TimeBank.HOURS} defaultMessage='Hours' />}
                                name='hours'
                                placeholder='Hours'
                                step={1}
                                integerOnly
                                required
                            />
                            <FormTextarea
                                label='Comment'
                                name='comment'
                                placeholder='Comment'
                                resize='vertical'
                                required={false}
                                multiple
                            />
                        </div>
                    </BaseForm>
                </Form>
            )}
        </Formik>
    );
}
